import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Space, Form, Input, Select, Button, Checkbox, Typography, 
         Divider, Radio, Slider, Steps} from "antd";
// import axios from "axios";
import ReactMarkdown from 'react-markdown'

import MarkdownIt from 'markdown-it';

import { mclOpen } from "../../api/Mcl";
import { Url, CAREPLAN_API_KEY, CAREPLAN_CLIENT_ID, 
  CAREPLAN_ACCESS_KEY, SCHEDULE_CONCIERGE_SESSION, CAREPLAN_THANK_YOU } from "../../constants/Global";

import { PasswordModal, ResubmitPlanModal, ResendPlanModal } from "../../components/PlanComponents"
import { use } from "react";


const { TextArea } = Input;
const { Title, Text } = Typography;



// > [Schedule your session here.](https://calendly.com/mycarelinq)

const labelMap_v0 = {
    caregiverLocation: {n: "caregiverLocation", q: "Where are you currently located?", p: "City and State, to help us curate services near you."},
    recipientLocation: {n: "recipientLocation", q: " Where is the person you're caring for located?", p: "City, State"},
    careDuration: {n: "careDuration", q: "How long have you been providing care?", p: "e.g. 6 months, 2 years"},
    weeklyHours: {n: "weeklyHours", q: "On average, how many hours per week do you spend caregiving?"},
    tasks: {n: "tasks", q: "Which caregiving tasks do you handle regularly?", 
      p: "transportation, meal preparation, bathing/dressing, medications, finances, companionship, medical decision making"},
    helpers: {n: "helpers", q: "Who else supports you in your caregiving role?", 
      p: "no one, family, paid professional, friends, community/faith-based support"},
    distress: {n: "distress", q: "What’s felt especially hard about caregiving lately?", 
      p: "This can be emotional, logistical, or anything else you'd like to share."},
    feeling: {n: "feeling", q: "What challenges or feelings have you been experiencing recently?", 
      p: "Feeling emotionally drained, Confused about what’s expected of you,Unsafe or emotionally harmed,Delaying your own needs,Feeling overwhelmed or close to burnout (Scale: Never → Always) "},
    confidence: {n: "confidence", q: " Which caregiving areas do you feel least confident or clear about?" },
    workImpact: {n: "workImpact", q: "Has caregiving affected your work or income in any way?", 
      p: "yes or no, please share more"},
    // expenseResponse: {n: "expenseResponse", q: "If a $1,000 care-related expense came up today, how would you manage it?", 
    //   p: "savings, credit, delay, ask family, unsure, please elaborate"},
    supportTools: {n: "supportTools", q: "Have you already tried any tools, services, or providers that didn't meet your expectations?"}, 
    supportTypes: {n: "supportTypes", q: "Which types of support would you consider accepting if offered?", 
      p: "emotional support, legal/financial help, care planning, respite care, finding housing, paid caregiver support,  please elaborate"},
    personalInsight: {n: "personalInsight", q: "(Optional) What’s something you wish others better understood about your caregiving situation?"},
    personalFuture: {n: "personalFuture", q: "(Optional) What worries you most about the future of this care situation?"},
    personalSituation: {n: "personalSituation", q: "(Optional) If you could reshape your caregiving situation in any way, what would that look like?"},
}

const labelMap = {
    caregiverLocation: {n: "caregiverLocation", q: "Where are you currently located?", 
      p: "City, State and Zip Code, to help us curate services near you.", d: "e.g., City, State Zip Code"},
    recipientLocation: {n: "recipientLocation", q: " Where is the person you're caring for located?", 
      p: "City, State and Zip Code, to help us recommend solutions in their area.", d: "e.g., City, State Zip Code"},
    careDuration: {n: "careDuration", q: "How long have you been providing care?", 
      p: `Being a family caregiver takes time, energy, and heart. 
          Whether you’ve just started or have been at it for years, the time you’ve invested matters — and we honor it.`, 
      d: "e.g., 6 months, 2 years"},
    weeklyHours: {n: "weeklyHours", q: "On average, how many hours per week do you spend caregiving?",
      d: "Number Input with 'hrs/week' suffix"},
    tasks: {n: "tasks", q: "Which caregiving tasks do you handle regularly?", 
      p: `Family caregiving is a full-time job — even when you’re not getting paid. 
          Tell us what’s on your plate so we can understand the true scope of what you're managing. 

          Some examples include: Meal preparation, Bathing or hygiene support, Dressing, Toileting, Mobility support, 
          Medication management, Transportation to appointments, Coordinating care/services, 
          Financial or legal management, Emotional support, Companionship or supervision, Home maintenance or running errands`,
      d: "Please describe"},
    helpers: {n: "helpers", q: "Who else supports you in your caregiving role?", 
      p: `Some families rally together. Others leave one person to carry it all. Whatever your situation looks like — 
          you’re not alone in it here. This helps us understand your support circle (or the gaps in it).`,
      d: "Please describe"},

    distress: {n: "distress", q: "What’s felt especially hard about caregiving lately?", 
      p: "This can be emotional, logistical, or anything else you'd like to share.",
      d: "Share what's been emotionally or logistically difficult"},
    feeling: {n: "feeling", q: "What challenges or feelings have you been experiencing recently?", 
      p: "",
      d: "Feel free to share more about these experiences or anything else that’s been difficult"},

    confusing: {n: "confusing", q: "Which parts of caregiving feel confusing or hard to handle?",
      p: `No one hands you a manual for family caregiving. It's okay not to know it all — many caregivers are figuring things 
          out as they go.  Let us know what’s felt confusing, overwhelming, or just unclear so we can better support you.

          Some examples could include: Knowing what to prioritize, getting clear on what insurance or benefits covers, legal 
          stuff (Wills, POA, Advance Directives, Estate Planning), diagnosis, medical terminology, treatment options, financial 
          planning, appointment scheduling or coordination, assisted living or long-term care`,
      d: "Why are these areas challenging?"},
    confidence: {n: "confidence", q: "How confident are you in managing your loved one’s care overall?", 
      p: "",
      d: "Feel free to share your thoughts"},

    workImpact: {n: "workImpact", q: "Has caregiving affected your work or income in any way?", 
      p: `Many family caregivers tell us that caregiving impacts their work and finances — often in invisible ways. 
          If you're comfortable, we’d love to understand how it’s affected you. Your answers help us match you with solutions that 
          won’t waste your time — or your wallet.`,
      d: "Tell us more"},

    supportTools: {n: "supportTools", q: "Have you already tried any tools, services, or providers that didn't meet your expectations?",
      p: `You’ve likely already tried things — some may have helped, others may have let you down. Feel free to share what didn’t work, 
          what felt like a waste of time or money, or what just wasn’t a fit. We’ll make sure not to recommend the same again.`,
      d: "List anything you’ve tried that wasn’t helpful or effective"}, 
    supportTypes: {n: "supportTypes", q: "Which types of support would you consider accepting if offered?", 
      p: `Family caregiving shouldn’t mean doing everything alone. If the right kind of help showed up — the kind that actually fit your 
          situation — what would you be open to exploring? Think of what might bring real relief or help you keep going.

          Some example could include: Emotional support or therapy, legal or financial help, care planning guidance, respite care (time off), 
          help finding housing, paid caregiver support`,
      d: "What kind of support would make the biggest difference right now?"},

    personalInsight: {n: "personalInsight", q: "What’s something you wish others better understood about your caregiving situation?",
      p: "",
      d: "Open-ended"},
    personalFuture: {n: "personalFuture", q: "What worries you most about the future of this care situation?",
      p: "",
      d: "Open-ended"},
    personalSituation: {n: "personalSituation", q: "If you could reshape your caregiving situation in any way, what would that look like?",
      p: "",
      d: "Describe your ideal scenario"},
}

const mclInitialValues = {
        [labelMap.caregiverLocation.n]: "San Francisco, California",
        [labelMap.recipientLocation.n]: "Sacramento, CA",
        [labelMap.careDuration.n]: "18 months",
        [labelMap.weeklyHours.n]: "15",
        [labelMap.tasks.n]: "I've had to manage more and more as I find things. I'm providing rides because it's generally unsafe for my mom to drive anymore. I'm running errands for her and now need to discuss how to manage her finances and be her POA for medical decisions.",
        [labelMap.helpers.n]: "my sister but she is busy and doesn't have a lot of time",
        [labelMap.distress.n]: "I really don't want to be doing this but there isn't any alternative. I feel like this is being forced on to me. My mother plays the victim. She is moody and doesn't express any appreciation for how much time and resources its taking me to care for her. I have no idea where or who to talk to about how to apply for POA. I don't have an understanding of how she is doing financially. I don't know where were records are or where she keeps her passwords. I'm really frustrated and I feel like I can't talk about it because I sound angry all the time. And no one understands.",
        [labelMap.feeling.n]: "I'm so tired and stressed about how much work this is. I have a feeling its only going to get worse. I really don't know what to do to help my mom. I really don't want to do this on my own. I've also lost sleep, and get migraines, and emotionally I'm all over the place.",
        [labelMap.confusing.n]: "Her estate stuff, POA, Advance Directive, insurance and what her wishes are.",
        [labelMap.confidence.n]: "Not confident in managing her overall care.",
        [labelMap.workImpact.n]: "I've taken time away from work to figure out what is going on in her home. Every time I visit I find something new. I'm spending my money on small things like drug store items, groceries, and transportation.",
        [labelMap.supportTools.n]: "Not yet. I'm still trying to figure where things are.",
        [labelMap.supportTypes.n]: "I really don't have the time or capacity to take this on. I'm not an expert and the time it takes to research each new thing totally distracts me from work, family, and my own needs.",
        [labelMap.personalInsight.n]: "It's really not fair to put something in this type of position. I feel like this situation is totally out of control and I have to make it better somehow.",
        [labelMap.personalFuture.n]: "If she has an accident that puts her in the hospital, I'm worried that I'll have to be her main support and I don't want that. I'm also worried that she will outlive her money.",
        [labelMap.personalSituation.n]: "Honestly, I wish I didn't have to be the one doing all of this for my mom. I'm so stressed and upset that is taking all my time and focus.",
}

const referralInitialValues = {        
        service_ident_prompt: "\n/no_think\nFrom this care plan, list service types needed.\n\nCare Plan:\n{care_plan}\n\nReturn a Python list.\n/no_think\n        ",
        referral_prompt: "\n/no_think\nFormat this provider data into a clean referral list, including both live and non-live sites.\n\nData:\n{providers}\n\nInclude provider name, URL, source and indicate live status. For live sites only, include snippet, relevancy and reason. Exclude Yelp and similar portals.\nGroup reply by service.\n/no_think\n        ",
        relevancy_prompt: "\n/no_think\nGiven a list of referrals for services:\n\n{raw_providers}\n\nAssign to each referral a relevancy tag with values \u201clow\u201d, \u201cmedium\u201d or \u201chigh\", by matching of a service, for which the referral is given, with title, formattedURL and snippet of the referral, and determining whether the referral is relevant for the service. Ignore the live status.\n\nFormat reply as original JSON with \u201crelevancy\u201d and \u201creason\u201d tags added for each referral.\n/no_think\n        ",
        routing_prompt: "\nGiven this list of services requested:\n\n{services}\n\nFor each service, return label \"external\".\nForm answer as JSON object in a form {{'service tag': 'external'}}.\n",
        url_search_template: "Top-rated {query} in {location}",
        vecstore_search_template: "Find {query} in {location}",
}

export const  CarePlanReco = (client = "") => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramEmail = queryParams.get('email');
  const paramEmployer = queryParams.get('employer') || 'Unknown';
  const paramOrderId = queryParams.get('order_id');
  const paramKey = queryParams.get('k');

  var initVal = {} 
  const emailDomain = paramEmail.split('@')[1];
  if (emailDomain === "mycarelinq.com") {
    initVal = mclInitialValues;
  }

  const [orderId, setOrderId] = useState(paramOrderId)
  const [event, setEvent] = useState(null)
  const [clientId, setClientId] = useState(client?.client)
  const [qa, setQA] = useState("");
  // const [prompt, setPrompt] = useState(initialPrompt);
  const [description, setDescription] = useState("mcl");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(false);
  const [mailing, setMailing] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(paramKey === CAREPLAN_ACCESS_KEY);
  const [authkey, setAuthkey] = useState(null);
  const [changekey, setChangekey] = useState(null);
  const [step, setStep] = useState(2);
  const [email, setEmail] = useState(paramEmail);
  const [org, setOrg] = useState(paramEmployer)
  const [planHtml, setPlanHtml] = useState("<></>");
  const [status, setStatus] = useState("")
  const [initialValues, setInitialValues] = useState(initVal)
  const [showResubmit, setShowResubmit] = useState(false)
  const [showResend, setShowResend] = useState(false)
  const [reco, setReco] = useState("")
  const [recoHtml, setRecoHtml] = useState("<></>")
  const [locationQA, setLocationQA] = useState("")
  const [options, setOptions] = useState({})

  const md = new MarkdownIt({
    html: true, // allow inline html to be rendered
    linkify: true,
    typographer: true
  });

  console.log(`cliendId:${JSON.stringify(clientId)}`)
  const validatePassword = () => {
    setAuthkey(changekey);
  }

  const handleResubmit = () => {
    setShowResubmit(false);
  }

  const handleResend = () => {
    setShowResend(false);
  }

  const handleProcess = async (values) => {
    console.log("handleProcess"+values)
    setLoading(true);
    const currentQA = "Questionnaire with Answers:\n\n" + Object.entries(values)
      .map(([key, value]) => `Question: ${labelMap[key].q} ${labelMap[key]?.p ? "("+labelMap[key]?.p+")" : ""} \nAnswer: ${value ? value: 'No answer'} \n`)
      .join('\n')

    setQA(currentQA)
    setPlan("");
    setLocationQA(values.recipientLocation)
    // console.log(Url.PLAN.concat("/ai-wb"));
    console.log(Url.PLAN.concat("/ai"));
    const eventId = event ? event?._id : null;
    console.log("eventId:" + eventId);
    try {      
      // const response = await mclOpen.post(Url.PLAN.concat("/ai-wb"),
      const response = await mclOpen.post(Url.PLAN.concat("/ai"),
        JSON.stringify({
          eventId: eventId,
          apiKey: CAREPLAN_API_KEY,
          orderId,
          clientId,
          email,
          org,
          qa: currentQA,
          // prompt,
          description,
        }),
        {
          headers: { "Content-Type": "application/json" },
        }
      )
      
      // console.log("Response:", response?.data);
      setPlan(response?.data || "No result returned.");
      setPlanHtml(md.render(response?.data ?? ""));
      setStep(3)
    } catch (err) {
      console.log(`Error processing request error: ${err.message}`);
      setPlanHtml(`Error processing request error: ${err.message}`);
      setShowResubmit(true)
    }
    setLoading(false);
  };

  const redirectTo = async ( url) => {

    const newTab = window.open(url, '_blank');

    if (!newTab) {
      // redirect in the same tab
      window.location.href = url;
    }  
  }

  const handleEmail = async () => {
    console.log("Sending email")
    try {      
      setMailing(true);
      const response = await mclOpen.post(Url.PLAN.concat("/ai-send"),
        JSON.stringify({
          clientId,
          email,
          org,
          qa,
          plan,
        }),
        {
          headers: { "Content-Type": "application/json" },
        }
      )

      console.log(`sending successful! clientId: ${clientId}`)
      setMailing(false)
      // setStep(4)
      switch (clientId) {
        case CAREPLAN_CLIENT_ID.ebg:
          console.log(`redirecting to ${CAREPLAN_THANK_YOU.ebg + "?email=" + email}`)
          redirectTo(CAREPLAN_THANK_YOU.ebg + `?email=${email}`)
          break;
        case CAREPLAN_CLIENT_ID.wanido:
          console.log(`redirecting to ${CAREPLAN_THANK_YOU.wanido + "?email=" + email}`)
          redirectTo(CAREPLAN_THANK_YOU.wanido + `?email=${email}`)
          break;
        case CAREPLAN_CLIENT_ID.corestream:
          console.log(`redirecting to ${CAREPLAN_THANK_YOU.corestream + "?email=" + email}`)
          redirectTo(CAREPLAN_THANK_YOU.corestream + `?email=${email}`)
          break;
        case CAREPLAN_CLIENT_ID.passport:
          console.log(`redirecting to ${CAREPLAN_THANK_YOU.passport + "?email=" + email}`)
          redirectTo(CAREPLAN_THANK_YOU.passport + `?email=${email}`)
          break;
        case CAREPLAN_CLIENT_ID.benefithub:
          console.log(`redirecting to ${CAREPLAN_THANK_YOU.benefithub + "?email=" + email}`)
          redirectTo(CAREPLAN_THANK_YOU.benefithub + `?email=${email}`)
          break;
        default:
          setStep(5);
      }

    } catch (err) {
      console.log("sending failed!!")
      setMailing(false)
      setShowResend(true)
    }
  }

  const handleOptions = async () => {
    setStep(4)
  }

  const handleReco = async (values) => {
    // console.log("handleReco values:", JSON.stringify(values))
    setOptions(values)
    try {      
      setRecommending(true);
      const payload = {...{
          clientId,
          email,
          org,
          qa,
          plan,
          location: locationQA
        }, ...values}
      // console.log("handleReco payload:", JSON.stringify(payload))
      const response = await mclOpen.post(Url.PLANRECO.concat("/ai-reco"),
        JSON.stringify(payload),
        {
          headers: { "Content-Type": "application/json" },
        }
      )

      console.log("Reco return successful")
      setReco(response?.data || "No result returned.");
      setRecoHtml(md.render(response?.data ?? ""));
      setStep(5)
      // console.log(`recommend successful! clientId: ${clientId}`)
      } catch (err) {
      console.log("recommend failed!!", JSON.stringify(err))
      setRecoHtml(`Error processing request error: ${err.message}`);
      setShowResubmit(true)
    }
    setRecommending(false)
}


const handleEmailOrgSubmit = (values) => {
  setEmail(values.email)
  setOrg(values.org)
  setStep(2)
}

const handleBegin = () => {
  setStep(2)
}

const handleStep = (value) => {
  setStep(value)
}

const sessionSchedule = () => {
  var location_session = "";
  switch (clientId) {
    case CAREPLAN_CLIENT_ID.ebg:
      location_session = SCHEDULE_CONCIERGE_SESSION.ebg;
      break;
    case CAREPLAN_CLIENT_ID.wanido:
      location_session = SCHEDULE_CONCIERGE_SESSION.wanido;
      break;
    case CAREPLAN_CLIENT_ID.mcl:
      location_session = SCHEDULE_CONCIERGE_SESSION.mcl;
      break;
    case CAREPLAN_CLIENT_ID.corestream:
      location_session = SCHEDULE_CONCIERGE_SESSION.corestream;
      break;
    case CAREPLAN_CLIENT_ID.passport:
      location_session = SCHEDULE_CONCIERGE_SESSION.passport;
      break;
   case CAREPLAN_CLIENT_ID.benefithub:
      location_session = SCHEDULE_CONCIERGE_SESSION.benefithub;
      break;
  }
  return location_session;  
} 

const { Title, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps
const { TextArea } = Input

const [form] = Form.useForm();



const ruleRequired = [
    { required: true, message: "Please fill out this field." }
]


const StyledDivider = ({ children }) => (
  <div
    style={{
      width: '100%',
      margin: '24px 0',
      backgroundColor: '#C5B1FF',
      padding: '16px',
      borderRadius: '4px',
      textAlign: 'center',
    }}
  >
    <Title
      level={2}
      style={{
        margin: 0,
        // fontFamily: 'Raleway, sans-serif',
        fontFamily: `'Raleway', 'Segoe UI', 'Helvetica', 'Arial', sans-serif'`,
        fontSize: '1.75rem',
        fontWeight: 400,
        color: '#000',
        textAlign: 'center',
      }}
    >
      {children}
    </Title>
  </div>
);

const labelStyle = {
  fontWeight: 600,
  fontSize: '1rem',
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
};

const hintTextStyle = {
  display: 'block',
  marginTop: '0px',     // adds space above the text
  marginBottom: '12px',  // keeps space below
  marginLeft: '8px',    // aligns with label
  color: '#6c6b6bff',
  fontSize: '0.85rem',
  fontWeight: 400,
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
};
  
const inputIndentStyle = {
  marginLeft: '8px',    // shifts the input box to the right
  width: 'calc(100% - 12px)', // subtracts the left margin
  paddingLeft: '12px',   // indents the text inside the input
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
};

const bulletListStyle = {
  listStyleType: 'disc',     // solid round bullets
  paddingLeft: '0px',       // indent bullets
  margin: 0,                 // remove default margin
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
};

const bulletItemStyle = {
  marginBottom: '8px',       // spacing between lines
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
};

const buttonWrapperStyle = {
  backgroundColor: '#000',     // black background
  padding: '1rem 2rem',        // thicker space around the button
  borderRadius: '8px',         // optional: rounded corners
  display: 'inline-block',     // shrink-wrap to button size
};

const buttonStyle = {
  backgroundColor: '#000',
  borderColor: '#000',
  color: '#fff',
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
  fontSize: '1rem',
  fontWeight: 500,
};

const disclaimerStyle = {
  fontFamily: 'Segoe UI, Helvetica Neue, Arial, sans-serif',
  fontSize: '0.7rem',
  fontWeight: 500,
};


return !isAuthorized ? <PasswordModal handleOk={validatePassword} modalShow={!isAuthorized} onKey={(val) => {setChangekey(val)}} status={status}/> : (
  // return (
    <div style={{ width: '90%', margin: "auto", padding: 24 }}>
      <ResubmitPlanModal handleOk={handleResubmit} modalShow={showResubmit}/>
      <ResendPlanModal handleOk={handleResend} modalShow={showResend} email={email}/>
      <Steps current={step} style={{ marginBottom: 24 }} onChange={handleStep}>
        <Step title="Welcome" />
        <Step title="Before You Begin" />
        <Step title="Questionnaire" />
        <Step title="Family Care Plan" />
        <Step title="Options" />
        <Step title="Referrals" />
        <Step title="Thank You" />
      </Steps>

      {step === 0 && (
        <Form layout="vertical" onFinish={handleEmailOrgSubmit}  initialValues={{ email: email, org: org }}>
          <Typography style={{ marginBottom: 24 }}>
            <Title level={3}>Welcome to My CareLinq</Title>
            <Paragraph>You’re in the right place.</Paragraph>
            <Paragraph>
              This experience is part of a family caregiving benefit provided by your employer in partnership with My CareLinq.
              We’ll help you create a personalized family caregiving plan, with expert support, curated services, and actionable next steps to help you and your family navigate care with confidence.
            </Paragraph>
            <Title level={4}>What to Expect</Title>
            <ul>
              <li>You’ll answer a few questions about yourself and your caregiving role</li>
              <li>It takes about 10–20 minutes</li>
              <li>You’ll receive a customized care plan that can be saved, updated, and shared</li>
            </ul>
            <Title level={4}>Verify Your Affiliation</Title>
          </Typography>
          <Form.Item name="org" label="Organization" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit">Proceed to Enrollment</Button>
        </Form>
      )}

      {step === 1 && (
          <div style={{ maxWidth: 700, margin: 'auto', padding: '2rem' }}>
          <Typography>
            <Title level={2}>Enterprise Enrollment Page</Title>
            <Title level={3}>Welcome to Your Caregiving Benefit</Title>
            <Paragraph>
              This caregiving benefit is brought to you by <strong>MyCareLinq</strong>.
            </Paragraph>

            <Title level={4}>What to Expect</Title>
            <ul>
              <li>Answer a few brief questions about your caregiving situation</li>
              <li>Estimated time: 10–20 minutes</li>
              <li>You’ll receive a personalized family care plan you can download, email, or refine later</li>
            </ul>

            <Title level={4}>Your Benefit Package Includes</Title>
            <ul>
              <li>Self-driven family care plan generation — complete a guided assessment and receive a customized plan you can update over time</li>
              <li>Schedule time with a Care Concierge — review your plan, explore solution options, and upgrade to full-service support if needed</li>
            </ul>

            <Title level={4}>Ready to Build Your Family Care Plan?</Title>

            <Title level={5}>Before You Begin</Title>
            <ul>
              <li>You’ll answer questions about your caregiving role, your loved one’s health, and any support systems you may already have</li>
              <li>This will take about 10–20 minutes</li>
              <li>You can pause, save, and return at any time</li>
            </ul>
            <Paragraph>
              <strong>Why we ask:</strong> This helps us tailor your care plan and recommendations — and prepares our team to support you if you want more help.
            </Paragraph>

            <Title level={5}>Tips Before You Start</Title>
            <ul>
              <li>Be honest — your information is private and secure</li>
              <li>Take your time — revisit questions if you need</li>
              <li>We’re here for you — your responses inform the best path forward</li>
            </ul>

            <div style={{ margin: '2rem 0' }}>
              <Button type="primary" size="large" onClick={handleBegin}>
                Begin My Family Care Plan
              </Button>
            </div>

          </Typography>
        </div>
      )}

      {step === 2 && (
        <Form layout="vertical" form={form} onFinish={handleProcess}
          initialValues={initialValues}
        >
        {/* <h3><bold>Let’s start with a few quick questions to get oriented—then you’ll have a chance to share more about your situation if you’d like.</bold></h3> */}
        {/* <Divider orientation="left" orientationMargin={0}>Core Questions</Divider> */}
        <StyledDivider>Let's Start With the Basics</StyledDivider>
          {/* <Form.Item label={labelMap.caregiverLocation.q} > */}
          <Form.Item label={<span style={labelStyle}>1. {labelMap.caregiverLocation.q}</span>}>
            {/* <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}> */}
            <Text style={hintTextStyle}>
              {labelMap.caregiverLocation.p}
            </Text>
            <Form.Item name={labelMap.caregiverLocation.n} rules={ruleRequired}>
              <Input style={inputIndentStyle} placeholder={labelMap.caregiverLocation.d} />
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>2. {labelMap.recipientLocation.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.recipientLocation.p}
            </Text>
            <Form.Item name={labelMap.recipientLocation.n} rules={ruleRequired}>
              <Input style={inputIndentStyle} placeholder={labelMap.recipientLocation.d}/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>3. {labelMap.careDuration.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.careDuration.p}
            </Text>
            <Form.Item name={labelMap.careDuration.n} rules={ruleRequired}>
              <Input style={inputIndentStyle} placeholder={labelMap.careDuration.d}/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>4. {labelMap.weeklyHours.q}</span>} >
          <Form.Item name={labelMap.weeklyHours.n} rules={ruleRequired}>
            <Input style={inputIndentStyle} placeholder={labelMap.weeklyHours.d}/>
          </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>5. {labelMap.tasks.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.tasks.p}
            </Text>
            <Form.Item name={labelMap.tasks.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.tasks.d} rows={4} />
            </Form.Item>
          </Form.Item>
 
          <Form.Item label={<span style={labelStyle}>6. {labelMap.helpers.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.helpers.p}
            </Text>
            <Form.Item name={labelMap.helpers.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.helpers.d} rows={2}/>
            </Form.Item>
          </Form.Item>

          <StyledDivider>What's been wearing you down lately?</StyledDivider>

          <Form.Item label={<span style={labelStyle}>7. {labelMap.distress.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.distress.p}
            </Text>
            <Form.Item name={labelMap.distress.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.distress.d} rows={4}/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>8. {labelMap.feeling.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.feeling.p}
            </Text>
            <Form.Item name={labelMap.feeling.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.feeling.d}/>
            </Form.Item>
          </Form.Item>

          <StyledDivider>Where could you use a hand?</StyledDivider>

          <Form.Item label={<span style={labelStyle}>9. {labelMap.confusing.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.confusing.p}
            </Text>
            <Form.Item name={labelMap.confusing.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.confusing.d} rows={2} />
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>10. {labelMap.confidence.q}</span>} >
            <Text style={hintTextStyle}>
              {labelMap.confidence.p}
            </Text>
            <Form.Item name={labelMap.confidence.n} rules={ruleRequired}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.confidence.d} rows={2} />
            </Form.Item>
          </Form.Item>

          <StyledDivider>Financial & Work Impact</StyledDivider>

          <Form.Item label={<span style={labelStyle}>11. {labelMap.workImpact.q}</span>}>
            <Text style={hintTextStyle}>
              {labelMap.workImpact.p}
            </Text>
            <Form.Item name={labelMap.workImpact.n} noStyle>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.workImpact.d} rows={3} />
            </Form.Item>
          </Form.Item>

          <StyledDivider>What Would Make This Easier?</StyledDivider>
          <Form.Item label={<span style={labelStyle}>12. {labelMap.supportTools.q}</span>}>
            <Text style={hintTextStyle}>
              {labelMap.supportTools.p}
            </Text>
            <Form.Item name={labelMap.supportTools.n}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.supportTools.d} rows={3} />
            </Form.Item>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>13. {labelMap.supportTypes.q}</span>}>
            <Text style={hintTextStyle}>
              {labelMap.supportTypes.p}
            </Text>
            <Form.Item name={labelMap.supportTypes.n}>
              <Input.TextArea style={inputIndentStyle} placeholder={labelMap.supportTypes.d} rows={3} />
            </Form.Item>
          </Form.Item>

          <StyledDivider>Your Story, In Your Words</StyledDivider>
            <Text style={hintTextStyle}>
              These last few questions are completely optional — but often the most powerful. They give us a glimpse into 
              what family caregiving really feels like for you: the hidden stress, the unspoken worry, or the vision you 
              hold for a better future.
            </Text>

          <Form.Item name={labelMap.personalInsight.n} label={<span style={labelStyle}>{labelMap.personalInsight.q}</span>}>
            <Input.TextArea style={inputIndentStyle} placeholder={labelMap.personalInsight.d} rows={4} />
          </Form.Item>

          <Form.Item name={labelMap.personalFuture.n} label={<span style={labelStyle}>{labelMap.personalFuture.q}</span>}>
            <Input.TextArea style={inputIndentStyle} placeholder={labelMap.personalFuture.d} rows={4} />
          </Form.Item>

          <Form.Item name={labelMap.personalSituation.n} label={<span style={labelStyle}>{labelMap.personalSituation.q}</span>}>
            <Input.TextArea style={inputIndentStyle} placeholder={labelMap.personalSituation.d} rows={4} />
          </Form.Item>

          <StyledDivider>Next Steps: Your Family Care Plan</StyledDivider>

          <Typography>
            <Divider style={labelStyle} orientation="left" orientationMargin={0}>Before You Submit</Divider>
            <Paragraph>
              <ul style={bulletListStyle}>
                <li style={bulletItemStyle}>Review your answers. You can go back and edit any section.</li>
                <li style={bulletItemStyle}>You don’t have to answer every question—just do your best.</li>
              </ul>
            </Paragraph>

            <Divider style={labelStyle} orientation="left" orientationMargin={0}>What Happens Next</Divider>
            <Paragraph>
              <ul style={bulletListStyle}>
                <li style={bulletItemStyle}>
                    Our Care Concierge system will generate a personalized Family Care Plan based on your responses.  
                    This will take up to 30 seconds to generate and display in this window.
                </li>
                <li style={bulletItemStyle}>A Care Concierge may reach out to you and offer personalized support.</li>
                <li style={bulletItemStyle}>If you feel like it would be helpful to speak with a Concierge, you may upgrade and purchase a consultation at any time.</li>
              </ul>            
            </Paragraph>

            <div style={{ margin: '2rem 0' }}>
              <Form.Item>
                <div style={buttonWrapperStyle}>
                <Button type="primary" size="medium" htmlType="submit"  loading={loading}
                  style={buttonStyle}
                >
                  Submit My Answers and Generate My Family Care Plan
                </Button>
                </div>
              </Form.Item>
            </div>

            <Paragraph type="secondary" style={disclaimerStyle}>
              By clicking Submit, your responses will be securely sent to our Care Concierge system. Within a few minutes, you’ll receive your personalized care plan via email or secure link.
            </Paragraph>
            <Paragraph type="secondary" style={disclaimerStyle}>
              Need help? Email us at <a href="mailto:support@mycarelinq.com">support@mycarelinq.com</a> 
            </Paragraph>
          </Typography>


        </Form>
      )}

      {step === 3 && (
        <div style={{ width: '80%', margin: 'auto', padding: '2rem' }}>
        <Title level={3}>Family Care Plan</Title>

          <Typography>
            {/* <ReactMarkdown>{plan}</ReactMarkdown> */}
            <div dangerouslySetInnerHTML={{ __html: planHtml }} />
          </Typography>

          { clientId == CAREPLAN_CLIENT_ID.wanido ?
            <></>
          :
            <Paragraph type="secondary" style={{ fontSize: '0.9rem' }}>
              <a href={sessionSchedule()} target="_blank" 
                rel="noopener noreferrer" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                Upgrade Here
              </a>
            </Paragraph>
          }

          <Button type="primary" size="large" onClick={handleOptions}>
            Referrals Options
          </Button>
        </div>
      )}

      {step === 4 && (
        <div style={{ width: '80%', margin: 'auto', padding: '2rem' }}>
        <Form layout="vertical" onFinish={handleReco}  initialValues={referralInitialValues}>
          <Typography style={{ marginBottom: 24 }}>
            <Title level={3}>Referrals Options</Title>
          </Typography>
          <Form.Item name="service_ident_prompt" label="Service Identification Prompt" rules={[{ required: true }]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
          <Form.Item name="referral_prompt" label="Referral Prompt" rules={[{ required: true}]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
          <Form.Item name="relevancy_prompt" label="Relevance Prompt" rules={[{ required: true}]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
          <Form.Item name="routing_prompt" label="Routing Prompt" rules={[{ required: true}]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
          <Form.Item name="url_search_template" label="Url Search Template" rules={[{ required: true}]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
          <Form.Item name="vecstore_search_template" label="Vector Search Template" rules={[{ required: true}]}>
            <Input.TextArea rows={5} wrap="soft"/>
          </Form.Item>
           <Button type="primary" size="large" htmlType="submit" loading={recommending}>Generate Referrals</Button>
        </Form>
        </div>
      )}

      {step === 5 && (
        <div style={{ width: '80%', margin: 'auto', padding: '2rem' }}>
        <Title level={3}>Referrals</Title>

          <Typography>
            {/* <ReactMarkdown>{plan}</ReactMarkdown> */}
            <div dangerouslySetInnerHTML={{ __html: recoHtml }} />
          </Typography>

          <Button type="primary" size="large" onClick={handleEmail} loading={mailing}>
            Email Family Care Plan and Referrals
          </Button>
        </div>
      )}

      {step === 6 && (
        <div className="max-w-2xl mx-auto p-8 text-center" role="main" aria-label="Thank You Page">
          <h1 className="text-3xl font-bold mb-4">Your Family Care Plan Is On Its Way</h1>

          <p className="text-lg mb-4">
            <strong>Thank You for Trusting MyCareLinq</strong>
          </p>

          <p className="text-md mb-4">
            Your personalized family caregiving plan has been generated and is on its way to your inbox.
            You can review it, return anytime to update, or share it with someone you trust.
          </p>

          <p className="text-md mb-4">
            If you requested a follow-up from a Care Concierge, we’ll be reaching out shortly.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
            <p className="text-sm text-blue-700">
              Check your email for a copy of your plan. If it’s not there within a few minutes, please check your spam folder or contact us at <strong>support@mycarelinq.com</strong>.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-300 p-4 rounded mb-4">
            <p className="text-sm text-gray-800">
              You may now <strong>close this tab</strong> or return to your organization’s benefits portal.<br />
              To update your plan later, visit <strong>www.mycarelinq.com</strong> and log in with your email.<br />
              Need help or prefer to talk to someone? Call us at <strong>1-408-214-2416</strong>.
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
            >
              Return to Homepage
            </button>
            <button
              onClick={() => {
                window.open(`${sessionSchedule()}`, "_blank");                }
              }
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Schedule a Concierge Follow-Up
            </button>
          </div>
        </div>      
      )}
    </div>
  );

}
